import {useLayoutEffect, useRef} from 'react'
import {clsx} from 'clsx'

import {testIdProps} from '@github-ui/test-id-props'
import {Popover} from '@primer/react'

import styles from './ValidationErrorPopover.module.css'
export interface ValidationErrorPopoverProps {
  /**
   * ID of the error message. Supply this to the relevant input's `aria-errormessage` prop.
   * Be sure to also set the input's `aria-invalid` prop.
   */
  id: string
  /**
   * Message to display. If `undefined`, the popover will be hidden. This is preferred over
   * not rendering the popover at all because it allows screen readers to watch for changes
   * in the message through `aria-live`.
   */
  message?: string
  /**
   * Optional test ID override. The default test ID is `validation-error-popover`.
   */
  testId?: string
  /**
   * The minimum amount of space (pixels) between the popover and the clipping container
   * boundaries.
   */
  margin?: number
  /**
   * Position of the popover relative to the container. Defaults to `'below'`.
   */
  position?: 'above' | 'below'
}

/**
 * Returns the closest parent that would clip the popover, or the page `body` if there is none.
 */
const getClippingParent = (popover: HTMLElement) => {
  let parent: HTMLElement | null = popover.parentElement
  while (parent) {
    const computedStyles = getComputedStyle(parent)
    if (computedStyles.overflow !== 'visible' && computedStyles.position !== 'static') {
      return parent
    }
    parent = parent.parentElement
  }
  return document.body
}

/**
 * Shows a validation error without breaking the surrounding layout.
 * This component should be used inside a container with `position: relative` (along with
 * the annotated input element) to position it properly.
 *
 * For accessibility, it is better for this component to always be mounted in the
 * document even if the relevant input is valid, and for the ID of this component to remain
 * constant. Instead of conditionally rendering this component, hide it by setting the `message`
 * to `undefined`.
 *
 * It is not inserted at the document root like overlays, so it is more accessible to screen
 * readers by remaining in the right place in the document flow. However, it is more succeptible
 * to clipping issues. The popover will make a 'best effort' to position itself so that it won't
 * get clipped by a container, but it's not foolproof so try to set containing elements to
 * `overflow: visible` if they are not `position: static`.
 */
export const ValidationErrorPopover = ({
  id,
  message,
  testId,
  margin = 4,
  position = 'below',
}: ValidationErrorPopoverProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const open = message !== undefined
  const isAbove = position === 'above'

  // Reposition the popover to fit inside the nearest clipping container.
  // Runs when message/position/margin changes - useLayoutEffect fires after DOM commit
  // so dimensions are accurate even with new content.
  // CSS handles base centering (left:50% + transform:translateX(-50%)).
  // JS calculates additional offset to prevent clipping.
  useLayoutEffect(() => {
    if (!open) return
    const popover = containerRef.current
    if (!popover) return

    const clippingParent = getClippingParent(popover)

    // Batch all layout reads first to avoid forced reflow
    const clippingParentRect = clippingParent.getBoundingClientRect()
    const maxWidth = clippingParentRect.width - margin * 2

    // Set max width first (only if changed to avoid style recalc)
    if (popover.style.maxWidth !== `${maxWidth}px`) {
      popover.style.maxWidth = `${maxWidth}px`
    }

    // Read popover dimensions after setting max width
    const popoverRect = popover.getBoundingClientRect()

    // Check if it clips and calculate additional offset
    const leftDistance = popoverRect.left - clippingParentRect.left
    const rightDistance = clippingParentRect.right - popoverRect.right

    const xOffset = leftDistance < margin ? -leftDistance + margin : rightDistance < margin ? rightDistance - margin : 0

    // Only set CSS variables - let CSS handle the transform composition
    const xOffsetValue = `${xOffset}px`
    if (popover.style.getPropertyValue('--x-offset') !== xOffsetValue) {
      popover.style.setProperty('--x-offset', xOffsetValue)
    }

    const caretOffset = `${-xOffset}px`
    if (popover.style.getPropertyValue('--caret-offset') !== caretOffset) {
      popover.style.setProperty('--caret-offset', caretOffset)
    }
  }, [open, message, margin, isAbove])

  return (
    <Popover
      open={open}
      caret={position === 'above' ? 'bottom' : 'top'}
      className={clsx(styles.Popover, position === 'above' ? styles.aboveCaret : styles.belowCaret)}
      ref={containerRef}
    >
      <Popover.Content
        as="p"
        aria-live="polite"
        id={id}
        className={clsx(styles.PopoverContent, position === 'above' ? styles.aboveCaret : styles.belowCaret)}
        {...testIdProps(testId ?? 'validation-error-popover')}
      >
        {message}
      </Popover.Content>
    </Popover>
  )
}
